'use client';

import { FC } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useSearchState } from 'xstate/hooks/useSearchState';
import { deleteRecentSearch } from 'xstate/actors/SearchActor';
import { useTranslations } from 'next-intl';
import SectionLabel from 'elements/SectionLabel';

// The persisted "RECENT SEARCHES" list. Clicking a row re-runs that query;
// the ✕ removes it. Renders nothing when there are no recents.
export const RecentSearches: FC<{ onPick: (query: string) => void }> = ({
  onPick,
}) => {
  const t = useTranslations();
  const { recentSearches } = useSearchState();

  if (recentSearches.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <SectionLabel className={styles.label}>{t('search.recent')}</SectionLabel>
      <ul>
        {recentSearches.map((query) => (
          <li key={query} className={styles.row}>
            <button
              type="button"
              className={styles.pick}
              onClick={() => onPick(query)}
            >
              <FontAwesomeIcon
                icon={faClockRotateLeft}
                className={styles.clock}
                aria-hidden="true"
              />
              <span className="truncate">{query}</span>
            </button>
            <button
              type="button"
              className={styles.remove}
              aria-label={t('search.removeRecent', { query })}
              onClick={() => deleteRecentSearch(query)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const styles = {
  container: 'space-y-0.5',
  label: 'px-3',
  row: 'group flex items-center rounded-[10px] hover:bg-ink-secondary/8',
  pick: 'flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left text-sm text-ink-primary focus:outline-none',
  clock: 'flex-none text-[13px] text-ink-tertiary',
  remove: classNames(
    'flex-none rounded-md px-3 py-2 text-xs text-ink-tertiary opacity-0 transition-opacity',
    'hover:text-ink-primary group-hover:opacity-100 focus:opacity-100 focus:outline-none',
  ),
};
